import React, {Component} from 'react';
import moment from "moment-timezone";
import {deleteResource, getResource, updateResource} from "../../../../data/actions/resource";
import {getSecondResource} from "../../../../data/actions/secondResource";
import LocalStorage from "../../../util/localStorage";
import CreateNewLocationDialog from "./create-location-dialog";
import InfoParagraph from "../../info-paragraph";
import Tippy from "@tippyjs/react";
import {
    ArrowPathIcon,
    ExclamationCircleIcon,
    ExclamationTriangleIcon,
    EyeIcon,
    InformationCircleIcon,
    PencilIcon,
    TrashIcon
} from '@heroicons/react/24/outline'
import ArrowsRightLeftIcon from '@heroicons/react/20/solid/ArrowsRightLeftIcon'
import NoRecordsTable from "../../no-records-found/no-records-table";
import ResourceTable from "../../resource-table";
import {
    CREATE_PERM,
    DEFAULT_CRUD_STATE,
    READ_PERM,
    STOP_TYPE_PICKUP,
    UPDATE_PERM
} from "../../../../util/util-constants";
import {Field, FieldsManager} from "../../../../data/services/fields";
import PageHeader from "../../layout/layout-components/page/page-header";
import {
    checkPerm,
    getDefaultTableOptions,
    getDefaultTableOptionsJSON,
    getProp,
    longTableColumn,
    openInNewTab,
    saveTableColumns
} from "../../../util/util-helpers";
import Pagination from "../../resource-table/table-components/pagination";
import ModalSaveResource from "../modal-save-resource";
import ModalConfirm from "../modal-confirm";
import Modal from "../index";
import TableCard from "../../resource-table/table-components/table-card";
import TableFilters from "../../resource-table/table-components/table-filters";
import {fillFieldsFromData} from "../../../util/util-fields";
import ActiveFilters from "../../resource-table/table-components/active-filters";
import Resources from "../../../../data/services/resources";
import MapDialog from "../modal-global/map-dialog";
import TableSettingsPopOver from "../../resource-table/table-components/table-settings-popover";
import TableOptionsDialog from "../../resource-table/table-components/table-options-dialog";
import RouteMapView from './route-map-view';
import {getStaleLoadMap} from './route-map-view/buildRouteSegments';
import RecalculateLoadMileage from '../recalculate-load-mileage';
import LoadsDispatchItemsModal from '../loads-dispatch-items-modal';
import {checkEmptyMileageWarning} from '../../../util/empty-mileage';
import Api from '../../../../data/services/api';
import {Loader} from '../../loader';
import FieldSwitch from '../../fields/field-switch';
import ModalDefault from "../modal-default";
import {cloneDeep} from "../../../util/util-vanilla";

class LocationsTab extends Component {
    constructor(props) {
        super(props);
        this.pagePath = this.props.location.pathname.slice(0, this.props.location.pathname.lastIndexOf('/')).substring(1) + "/locations";

        this.state = {
            tableOptions: getDefaultTableOptions(this.getFields(), {
                behaviour: {
                    rowSelect: true,
                    canAdjustWidth: true,
                    hasMenu: true
                },
                style: {
                    condensed: true,
                    isGPUAccelerated: true
                },
            }, this.pagePath, this.props.translate),
            fields: this.getFields(),
            ...DEFAULT_CRUD_STATE,
            limit: 100,
            sortBy: "LocationDate",
            sort: "DESC",
            showAllLocations: false,
            isMoving: false,
            queryFilterFields: this.getQueryFilterFields(),

            locationsModalOpen: false,
            selectedItem: undefined,
            isMapDialogOpen: false,
            isColumnsDialogVisible: false,
            viewMode: 'table',
            recalculateOpen: false,
            recalculateLoadID: null,
            recalculateStopsCombined: null,
            recalculatePrevLastTruckLocationID: null,

            dispatchModalOpen: false,
            dispatchModalItem: null,

            bulkRecalculateOpen: false,
            bulkRecalculateLoads: [],   // [{LoadID, LoadNumber, TruckLocationID, selected}]
            bulkRecalculateLoading: false,
            bulkRecalculateDone: false,
        }
    }

    /** Lifecycle
     ================================================================= */
    componentDidMount() {
        this.fetchData();
        if (this.props.resourcePath === Resources.TrucksLocations) {
            this.props.dispatch(getSecondResource({
                user: LocalStorage.get("user"),
                resource: Resources.DispatchSettings,
                query: {}
            }));
        }
    }

    componentDidUpdate = (prevProps, prevState) => {
        if (prevProps.match?.params?.id !== this.props.match?.params?.id) {
            this.setState({viewMode: 'table'});
        }
        if ((prevState.sort !== this.state.sort) || (prevState.sortBy !== this.state.sortBy && this.state.sortBy !== "")) {
            this.fetchData();
        }
    };

    /** Data events
     ================================================================= */
    fetchData = () => {
        this.props.dispatch(getResource({
            user: LocalStorage.get("user"),
            resource: this.getResource(),
            query: this.getQuery()
        }));
    };

    /** UI Events
     ================================================================= */

    handleFilterChange = (name, val) => {
        this.setState({
            [name]: val,
            offset: 0,
            paginationPage: 1,
        }, this.fetchData)
    }

    handleUpdateOffset = (offset, pagenum) => {
        this.setState({
            offset: offset,
            paginationPage: pagenum
        }, this.fetchData);
    }

    handleUpdateSort = (sortBy) => {
        this.setState({
            sortBy: sortBy,
            sort: (this.state.sortBy === sortBy) ? (this.state.sort === 'ASC' ? 'DESC' : 'ASC') : 'ASC'
        }, () => this.fetchData())
    }

    handleShowLocationDetails = (item) => {
        this.setState({
            selectedItem: item,
            isMapDialogOpen: true
        })
    }

    handleOpenDispatchModal = (item) => {
        this.setState({dispatchModalOpen: true, dispatchModalItem: {LoadNumber: item.LoadNumber, Switches: item.Switches ?? null}});
    }

    handleOpenBulkRecalculate = (dataItems) => {
        const stopLabel = (stop) => stop
            ? [stop.LocationName, stop.CityName, stop.State].filter(Boolean).join(', ')
            : null;

        const firstStopByLoad = new Map(); // LoadID → earliest stop item
        const loadsWithOutOfOrderStop = new Set();

        for (const item of dataItems) {
            if (!item.LoadID) continue;
            const existingFirst = firstStopByLoad.get(item.LoadID);
            if (!existingFirst || new Date(item.LocationDate) < new Date(existingFirst.LocationDate)) {
                firstStopByLoad.set(item.LoadID, item);
            }
            if (this._outOfOrderStopIDs?.has(item.TruckLocationID)) {
                loadsWithOutOfOrderStop.add(item.LoadID);
            }
        }

        // All stops sorted chronologically — used to find the true last position
        // before each load's first stop, even when loads are interleaved.
        const allStopsSorted = [...dataItems]
            .filter(it => it.LoadID)
            .sort((a, b) => new Date(a.LocationDate) - new Date(b.LocationDate));

        const sortedFirstStops = [...firstStopByLoad.values()]
            .sort((a, b) => new Date(a.LocationDate) - new Date(b.LocationDate));

        const loads = sortedFirstStops.map((item) => {
            const stats = this._loadStatsMap?.get(item.LoadID);
            const staleInfo = this._staleLoadMap?.get(item.LoadID);
            const hasEmptyWarning = !!checkEmptyMileageWarning(stats?.emptyMiles, stats?.totalMiles, this._dispatchSettings);
            const hasOutOfOrder = loadsWithOutOfOrderStop.has(item.LoadID);
            const hasStale = !!staleInfo;
            const isFlagged = hasEmptyWarning || hasOutOfOrder || hasStale;

            // Find the globally latest stop (from any load) whose LocationDate is
            // strictly before this load's first stop — correctly handles interleaved
            // loads where the immediately preceding stop belongs to a different
            // still-ongoing load.
            const firstStopDate = new Date(item.LocationDate);
            let prevStop = null;
            for (let i = allStopsSorted.length - 1; i >= 0; i--) {
                const s = allStopsSorted[i];
                if (s.LoadID === item.LoadID) continue;
                if (new Date(s.LocationDate) < firstStopDate) {
                    prevStop = s;
                    break;
                }
            }

            return {
                LoadID: item.LoadID,
                LoadNumber: item.LoadNumber,
                TruckLocationID: prevStop?.TruckLocationID ?? null,
                selected: isFlagged,
                isFlagged,
                hasEmptyWarning,
                hasOutOfOrder,
                hasStale,
                emptyMiles: stats?.emptyMiles ?? null,
                calculatedFrom: staleInfo ? stopLabel(staleInfo.dispatchRefStop) : null,
                shouldUse: staleInfo ? stopLabel(staleInfo.actualLastStop) : null,
            };
        });
        this.setState({bulkRecalculateOpen: true, bulkRecalculateLoads: loads.reverse(), bulkRecalculateDone: false});
    }

    handleBulkRecalculateSubmit = async () => {
        const selected = this.state.bulkRecalculateLoads.filter(l => l.isFlagged && l.selected && l.TruckLocationID != null);
        if (!selected.length) return;
        this.setState({bulkRecalculateLoading: true});
        await Api.updateResource(LocalStorage.get('user'), {
            LoadIDs: selected.map(l => l.LoadID),
            TruckLocationIDs: selected.map(l => l.TruckLocationID),
        }, 'load/locations/recalculate');
        this.setState({bulkRecalculateLoading: false, bulkRecalculateDone: true});
        this.fetchData();
    }

    handleToggleColumnSettingsDialog = () => {
        this.setState({
            isColumnsDialogVisible: !this.state.isColumnsDialogVisible
        })
    }

    handleToggleLocationsModal = (item = null, isMoving) => {
        this.setState({
            showAllLocations: false,
            selectedItem: item,
            isMoving: isMoving,
            locationsModalOpen: !this.state.locationsModalOpen
        })
    }

    handleToggleNonManualEditDialog = (item = null) => {
        this.setState({
            editModalOpen: !this.state.editModalOpen,
            selectedItem: item
        })
    }

    handleToggleConfirmModal = (item = null) => {
        this.setState({
            confirmModalOpen: !this.state.confirmModalOpen,
            selectedItem: item
        })
    }

    handleFilterInputChange = (name, value) => {
        this.setState({
            queryFilterFields: FieldsManager.updateField(this.state.queryFilterFields, name, value),
            offset: 0,
            paginationPage: 1
        }, () => {
            this.fetchData()
        });
    }

    handleClearFiltersClick = () => {
        const queryFilterFields = cloneDeep(this.state.queryFilterFields)
        const excludedFields = ['limit']

        Object.values(queryFilterFields).filter(it => !excludedFields.includes(it.name)).forEach(it => {
            FieldsManager.updateField(queryFilterFields, it.name, '')
        })

        this.setState({
            queryFilterFields: queryFilterFields,
            offset: 0,
            paginationPage: 1
        }, () => this.fetchData())
    }

    setTableColumnOptions = (columns) => {
        let tableOptions = Object.assign({}, this.state.tableOptions)

        tableOptions.columns = columns.reduce((memo, it) => {
            memo[it.name] = it
            return memo
        }, {})

        this.setState({
            tableOptions,
            isColumnsDialogVisible: false
        }, () => saveTableColumns(this.pagePath, tableOptions))
    }

    getDefaultTableColumnOptions = () => {
        return getDefaultTableOptionsJSON(this.getFields(), {}, this.props.translate);
    }

    setOptions = (options) => {
        this.setState({
            tableOptions: options
        }, () => saveTableColumns(this.pagePath, options))
    }
    /** Helpers
     ================================================================= */
    getNonManualEditFields = (item = null) => {
        const fieldTemplates = {
            LocationDate: new Field('LocationDate', '', [], false, 'datetime', {addContainerClass: "col-span-full"}),
            Notes: new Field('Notes', '', [], false, 'textarea', {addContainerClass: "col-span-full"})
        }

        return item ? fillFieldsFromData(fieldTemplates, item) : fieldTemplates;
    }

    getFields = () => {
        return {
            LocationName: new Field("LocationName", '', [], false, 'text', {omitSort: true}),
            LocationDate: new Field("LocationDate", '', [], false, 'datetime', {
                columnName: "Date",
                omitSort: true
            }),
            AddressName: new Field("AddressName", '', [], false, 'text', {omitSort: true}),
            CityName: new Field("CityName", '', [], false, 'text', {omitSort: true}),
            State: new Field("State", '', [], false, 'text', {omitSort: true}),
            PostalCode: new Field("PostalCode", '', [], false, 'text', {omitSort: true}),
            CountryName: new Field("CountryName", '', [], false, 'text', {omitSort: true}),
            Source: new Field("Source", '', [], false, 'text', {omitSort: true}),
            LoadID: new Field('LoadID', '', [], false, 'custom', {
                omitSort: true,
                render: (item) => {
                    const staleInfo = this.props.resourcePath === Resources.TrucksLocations
                        ? this._staleLoadMap?.get(item.LoadID)
                        : null;
                    const stopLabel = (stop) => stop
                        ? [stop.LocationName, stop.CityName, stop.State].filter(Boolean).join(', ')
                        : null;
                    const loadStats = this._loadStatsMap?.get(item.LoadID);
                    const isFirstStop = this._loadFirstStopIDs?.get(item.LoadID) === item.TruckLocationID;
                    const emptyWarning = isFirstStop
                        ? checkEmptyMileageWarning(loadStats?.emptyMiles, loadStats?.totalMiles, this._dispatchSettings)
                        : null;
                    return (
                        <div className="flex items-center gap-1">
                            {emptyWarning && (
                                <Tippy
                                    content={
                                        <div style={{fontSize: '12px', lineHeight: '1.5'}}>
                                            <div style={{fontWeight: '600', marginBottom: '2px'}}>High empty mileage</div>
                                            <div>{emptyWarning.emptyMiles.toFixed(1)} mi empty{emptyWarning.limitStr ? ` (limit: ${emptyWarning.limitStr})` : ''}</div>
                                        </div>
                                    }
                                >
                                    <ExclamationCircleIcon className={emptyWarning.critical ? "w-5 h-5 text-red-500 shrink-0 cursor-default" : "w-4 h-4 text-orange-500 shrink-0 cursor-default"}/>
                                </Tippy>
                            )}
                            {staleInfo && isFirstStop && (
                                <Tippy
                                    interactive
                                    content={
                                        <div style={{fontSize: '12px', lineHeight: '1.5'}}>
                                            <div style={{fontWeight: '600', marginBottom: '4px'}}>Empty mileage may be inaccurate</div>
                                            {staleInfo.dispatchRefStop && (
                                                <div>Calculated from: {stopLabel(staleInfo.dispatchRefStop)}</div>
                                            )}
                                            <div>Should use: {stopLabel(staleInfo.actualLastStop)}</div>
                                        </div>
                                    }
                                >
                                    <ExclamationTriangleIcon className="w-4 h-4 text-red-500 shrink-0 cursor-default"/>
                                </Tippy>
                            )}
                            {this._outOfOrderStopIDs?.has(item.TruckLocationID) && (
                                <Tippy content={
                                    <div style={{fontSize: '12px', lineHeight: '1.5'}}>
                                        <div style={{fontWeight: '600', marginBottom: '2px'}}>Load stops may not be in order</div>
                                        <div>This is sometimes expected for drop &amp; hook loads.</div>
                                    </div>
                                }>
                                    <InformationCircleIcon className="w-4 h-4 text-blue-400 shrink-0 cursor-default"/>
                                </Tippy>
                            )}
                            {item.LoadNumber && checkPerm(Resources.Loads, READ_PERM)
                                ? <button className="text-primary hover:underline" onClick={() => openInNewTab(`/loads/info/${item.LoadID}`)}>{item.LoadNumber}</button>
                                : <span>{item.LoadNumber}</span>
                            }
                            {item.LoadID && isFirstStop && (
                                <Tippy content="Dispatch details">
                                    <button
                                        className="ml-1 p-0.5 rounded hover:bg-tm-gray-100"
                                        onClick={(e) => { e.stopPropagation(); this.handleOpenDispatchModal(item); }}
                                    >
                                        <ArrowsRightLeftIcon className="text-green-600 w-4 h-4"/>
                                    </button>
                                </Tippy>
                            )}
                        </div>
                    );
                }
            }),
            Notes: new Field('Notes', '', [], false, 'text', {
                omitSort: true,
                render: (item) => (
                    <>{longTableColumn(item.Notes)}</>
                )
            })
        }
    }

    getPrimaryKey = () => {
        return this.props.primaryKey ?? ""
    }

    getQueryFilterFields = () => {
        return {
            StartDate: new Field('StartDate', moment().subtract(14, 'days').format('YYYY-MM-DD'), [], false, 'date', {labelType: 'stack'}),
            EndDate: new Field('EndDate', '', [], false, 'date', {labelType: 'stack'}),
            limit: new Field('limit', '100', [''], false, 'select', {hideLabel: true})
        }
    }

    getQuery = () => {
        const queryFields = this.state.queryFilterFields;
        return {
            id: this.getID(),
            limit: this.state.limit,
            offset: this.state.offset,
            sort: this.state.sort,
            sortBy: this.state.sortBy,
            ...FieldsManager.getFieldKeyValues(queryFields),
        };
    }

    getID = () => {
        return this.props.match.params.id
    }

    getResource = () => {
        return this.props.resourcePath
    }

    render() {
        const {translate, resource} = this.props;
        const dataItems = getProp(resource.data, `list`, [])
        const onLoad = !!getProp(resource.data, `onLoad`, true)
        const count = getProp(resource.data, `count`, 0)

        // Computed once per render; accessed from field render functions via this._staleLoadMap.
        this._staleLoadMap = this.props.resourcePath === Resources.TrucksLocations
            ? getStaleLoadMap(dataItems)
            : new Map()

        this._dispatchSettings = null;
        this._loadStatsMap = new Map();
        this._loadFirstStopIDs = new Map(); // LoadID → TruckLocationID of earliest stop
        if (this.props.resourcePath === Resources.TrucksLocations) {
            this._dispatchSettings = getProp(this.props.secondResource, 'data', null);
            // Track per-load stats and the earliest stop (by date) per load.
            // EmptyMiles is a load-level attribute (same on every stop), but the warning
            // icon is shown only on the initial stop — the one that incurred the empty miles.
            const firstStopByLoad = new Map(); // LoadID → {TruckLocationID, LocationDate}
            for (const item of dataItems) {
                if (!item.LoadID) continue;
                if (!this._loadStatsMap.has(item.LoadID)) {
                    this._loadStatsMap.set(item.LoadID, {
                        totalMiles: item.TotalMiles ? parseFloat(item.TotalMiles) : 0,
                        emptyMiles: item.EmptyMiles ? parseFloat(item.EmptyMiles) : 0,
                    });
                }
                const existing = firstStopByLoad.get(item.LoadID);
                if (!existing || new Date(item.LocationDate) < new Date(existing.LocationDate)) {
                    firstStopByLoad.set(item.LoadID, {TruckLocationID: item.TruckLocationID, LocationDate: item.LocationDate});
                }
            }
            this._loadFirstStopIDs = new Map(
                [...firstStopByLoad.entries()].map(([loadID, info]) => [loadID, info.TruckLocationID])
            );
        }

        let totalLoadedMiles = 0;
        let totalEmptyMiles = 0;
        for (const [, stats] of this._loadStatsMap) {
            totalLoadedMiles += stats.totalMiles;
            totalEmptyMiles += stats.emptyMiles;
        }

        // Detect stops where the same LoadID reappears after stops from a different load
        // came in between — i.e., truly interleaved loads (e.g. drop-and-hook gone wrong).
        // Using LoadID reappearance instead of load number comparison because load numbers
        // are not guaranteed to be assigned in chronological dispatch order.
        this._outOfOrderStopIDs = new Set();
        if (this.props.resourcePath === Resources.TrucksLocations) {
            const sorted = [...dataItems]
                .filter(it => it.LoadID && it.LoadNumber)
                .sort((a, b) => new Date(a.LocationDate) - new Date(b.LocationDate));
            const seenLoadIDs = new Set();
            let currentLoadID = null;
            sorted.forEach(it => {
                if (it.LoadID === currentLoadID) return;
                if (seenLoadIDs.has(it.LoadID)) {
                    this._outOfOrderStopIDs.add(it.TruckLocationID);
                }
                seenLoadIDs.add(currentLoadID);
                currentLoadID = it.LoadID;
            });
        }

        const unit = getProp(resource.data, `unit`, {})

        return (
            <div>
                <div className={"flex justify-between mb-2"}>
                    <div className={"flex items-center justify-center"}>
                        <ActiveFilters
                            filterFields={this.state.queryFilterFields}
                            onLabelClick={this.handleFilterInputChange}
                            onClearFiltersClick={this.handleClearFiltersClick}
                            translate={translate}
                        />
                    </div>

                    <PageHeader>
                        {!resource.isLoading && (
                            <div className="flex">
                                {checkPerm(this.getResource(), CREATE_PERM) && (
                                    <Tippy content={<span>Mileage will NOT be reported or written to tax logs, only position of the asset will be updated. Associated unit position will be updated as well.</span>}>
                                        <div className="btn btn-primary"
                                             onClick={() => this.handleToggleLocationsModal({}, false)}>{translate("btn.UpdateLocation")}
                                        </div>
                                    </Tippy>
                                )}

                                {(checkPerm(this.getResource(), CREATE_PERM) && this.props.resourcePath === Resources.TrucksLocations) && (
                                    <Tippy
                                           content={
                                               onLoad ?
                                                   <span>{translate(this.getResource())} cannot currently be changed since asset is on load which is dispatched</span>
                                                   : (!unit?.DriverID ? <span>Cannot move asset without assigned driver in the dispatch manager.</span> : <span>Mileage will be reported and written to the tax logs. Associated unit position will be updated as well.</span>)}>
                                        <div
                                            className={"btn btn-primary ml-2 " + (!!onLoad || !unit?.DriverID ? "disabled" : '')}
                                            onClick={() => (!onLoad && !!unit?.DriverID) && this.handleToggleLocationsModal({}, true)}>{translate("btn.MoveAsset")}
                                        </div>
                                    </Tippy>
                                )}

                                {this.props.resourcePath === Resources.TrucksLocations && (
                                    <div className="flex rounded border border-tm-gray-300 overflow-hidden ml-2">
                                        <button
                                            className={"px-3 py-1 text-sm " + (this.state.viewMode === 'table' ? "bg-primary text-white" : "bg-inverse text-tm-gray-700 hover:bg-tm-gray-50")}
                                            onClick={() => this.setState({viewMode: 'table'})}
                                        >
                                            Table
                                        </button>
                                        <button
                                            className={"px-3 py-1 text-sm border-l border-tm-gray-300 " + (this.state.viewMode === 'map' ? "bg-primary text-white" : "bg-inverse text-tm-gray-700 hover:bg-tm-gray-50")}
                                            onClick={() => this.setState({viewMode: 'map'})}
                                        >
                                            Route Map
                                        </button>
                                    </div>
                                )}

                                {this.props.resourcePath === Resources.TrucksLocations && checkPerm(Resources.LoadPostMileage, UPDATE_PERM) && (
                                    <button
                                        className="btn btn-outline ml-2"
                                        onClick={() => this.handleOpenBulkRecalculate(dataItems)}
                                    >
                                        <ArrowPathIcon className="w-4 h-4 mr-1.5"/>
                                        Re-calculate empty mileage
                                    </button>
                                )}

                                <TableSettingsPopOver
                                    options={this.state.tableOptions}
                                    setOptions={this.setOptions}
                                    toggleColumnSettingsDialog={this.handleToggleColumnSettingsDialog}
                                    translate={translate}
                                />
                            </div>
                        )}
                    </PageHeader>
                </div>


                {this.props.resource.errorMessage && (
                    <div className="row">
                        <div className="col-md-6">
                            <InfoParagraph
                                type="danger"
                            >
                                {translate(this.props.resource.errorMessage)}
                            </InfoParagraph>
                        </div>
                    </div>
                )}

                {this.props.resourcePath === Resources.TrucksLocations && this.state.viewMode === 'map' && (
                    <>
                        {count > dataItems.length && (
                            <div className="mb-2 px-3 py-2 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
                                Showing {dataItems.length} of {count} locations. Increase the limit or paginate to see the full route.
                            </div>
                        )}
                        <RouteMapView
                            locations={dataItems}
                        />
                    </>
                )}

                <div className={this.props.resourcePath === Resources.TrucksLocations && this.state.viewMode === 'map' ? 'hidden' : ''}>
                {this.props.resourcePath === Resources.TrucksLocations && this._loadStatsMap?.size > 0 && (
                    <div className="flex gap-3 mb-2 text-sm flex-wrap">
                        <div className="flex items-center gap-1.5 bg-blue-50 border border-blue-200 rounded-md px-3 py-1.5">
                            <span className="font-semibold text-blue-700">{totalLoadedMiles.toFixed(1)} mi</span>
                            <span className="text-blue-500">loaded</span>
                        </div>
                        <div className="flex items-center gap-1.5 bg-green-50 border border-green-200 rounded-md px-3 py-1.5">
                            <span className="font-semibold text-green-700">{totalEmptyMiles.toFixed(1)} mi</span>
                            <span className="text-green-500">empty</span>
                        </div>
                        {(totalLoadedMiles + totalEmptyMiles) > 0 && (
                            <div className="flex items-center gap-1.5 bg-tm-gray-50 border border-tm-gray-200 rounded-md px-3 py-1.5">
                                <span className="font-semibold text-tm-gray-700">{Math.round(totalEmptyMiles / (totalLoadedMiles + totalEmptyMiles) * 100)}%</span>
                                <span className="text-tm-gray-500">empty ratio</span>
                            </div>
                        )}
                    </div>
                )}
                <TableCard>
                    <TableFilters
                        hideLimit
                        onRefreshTable={this.fetchData}
                        isLoading={resource.isLoading}
                        filterFields={this.state.queryFilterFields}
                        handleInputChange={this.handleFilterInputChange}
                        translate={translate}
                        containerClass="flex gap-3"
                        containerClassSm="hidden"
                    />
                    <ResourceTable
                        tableKey={this.props.primaryKey}
                        data={dataItems}
                        count={count}

                        fields={this.props.tableFields ?? this.state.fields}
                        translate={this.props.translate}
                        isLoading={resource.isLoading}

                        limit={this.state.queryFilterFields?.limit?.value ?? this.state.limit}
                        offset={this.state.offset}
                        page={this.state.paginationPage}
                        onOffsetChange={this.handleUpdateOffset}

                        options={this.state.tableOptions}
                        saveTableOptions={this.setOptions}

                        sort={this.state.sort}
                        sortBy={this.state.sortBy}
                        onSortChange={this.handleUpdateSort}

                        actions={[
                            {
                                title: () => translate('btn.quick_view'),
                                visible: (item) => (item.Longitude || item.Latitude),
                                action: this.handleShowLocationDetails,
                                icon: () => EyeIcon,
                            },
                            {
                                title: () => translate('btn.delete'),
                                visible: (item) => item.Source === "Manual",
                                action: this.handleToggleConfirmModal,
                                icon: () => TrashIcon,
                            },
                        ]}
                    />
                    {!(!dataItems.length && !resource.isLoading) && (
                        <div
                            className="bg-inverse px-4 py-3 flex items-center justify-between ring-1 ring-black/10 sm:px-6 h-16 rounded-b-lg"
                        >
                            <Pagination
                                count={count}
                                isLoading={resource.isLoading}
                                handleQueryChange={
                                    (name, value, currentPage) => name === "offset"
                                        ? this.handleUpdateOffset(value, currentPage)
                                        : this.handleFilterInputChange(name, value)
                                }
                                pageOffset={this.state.offset}
                                queryFields={this.state.queryFilterFields}
                                translate={translate}
                            />
                        </div>
                    )}

                    <NoRecordsTable
                        show={(dataItems.length === 0) && !resource.isLoading}
                        canCreate={false}
                        title={translate("text.no_matching_records")}
                        className={"pb-12 pt-16 px-6"}
                    />
                </TableCard>
                </div>

                <TableOptionsDialog
                    show={this.state.isColumnsDialogVisible}
                    pagePath={this.pagePath}
                    columns={this.state.tableOptions.columns}
                    setTableColumnOptions={this.setTableColumnOptions}
                    getDefaultTableColumnOptions={this.getDefaultTableColumnOptions}
                    onClose={this.handleToggleColumnSettingsDialog}
                    translate={translate}
                />

                <ModalSaveResource
                    title={"Edit Location"}
                    widthClass="max-w-md"
                    gridColsClass="grid-cols-3"
                    show={this.state.editModalOpen}
                    onClose={() => this.handleToggleNonManualEditDialog()}
                    fields={this.getNonManualEditFields(this.state.selectedItem)}
                    onSubmit={(params) => {
                        if (params) {
                            params.id = this.getID();
                            params[this.getPrimaryKey()] = this.state.selectedItem[this.getPrimaryKey()];
                            this.props.dispatch(updateResource({
                                user: LocalStorage.get("user"),
                                query: this.getQuery(),
                                params: params,
                                resource: this.getResource(),
                                piggyResource: this.getResource(),
                                errorMessage: true, successMessage: "Location updated.",
                            }));
                            this.handleToggleNonManualEditDialog()
                        }
                    }}
                    translate={this.props.translate}
                />

                <ModalConfirm
                    title={"Confirm"}
                    show={!!this.state.confirmModalOpen}
                    text={"Are you sure you want to delete this location?"}
                    onClose={this.handleToggleConfirmModal}
                    buttonLabel={translate("btn.confirm")}
                    closeButtonLabel={'Cancel'}
                    translate={translate}
                    onConfirm={() => {
                        this.props.dispatch(deleteResource({
                            user: LocalStorage.get("user"),
                            query: Object.assign({
                                [this.getPrimaryKey()]: this.state.selectedItem[this.getPrimaryKey()]
                            }, this.getQuery()),
                            resource: this.getResource(),
                            piggyResource: this.getResource(),
                            errorMessage: true, successMessage: "Location deleted."
                        }));
                        this.handleToggleConfirmModal(false)
                    }}
                />

                <Modal
                    show={this.state.locationsModalOpen}
                    onClose={this.handleToggleLocationsModal}
                    widthClass={'max-w-[1600px]'}
                >
                    <CreateNewLocationDialog
                        onClose={this.handleToggleLocationsModal}
                        translate={translate}
                        dispatch={this.props.dispatch}
                        resourcePath={this.getResource()}
                        resourceID={this.getID()}
                        getQuery={this.getQuery}
                        item={this.state.selectedItem}
                        additionalParams={{
                            isMoving: this.state.isMoving ? 1 : 0
                        }}
                        htmlAfterHeader={(
                            <div className={"py-2 px-6"}>{this.state.isMoving ? (
                                <InfoParagraph>{translate("text.isMoving")}</InfoParagraph>) : (
                                <InfoParagraph>{translate("text.notMoving")}</InfoParagraph>)}</div>
                        )}
                    />
                </Modal>

                {this.state.isMapDialogOpen && (
                    <MapDialog
                        mapData={this.state.selectedItem}
                        onClose={() => {
                            this.setState({
                                isMapDialogOpen: false,
                                selectedItem: undefined
                            })
                        }}
                        translate={translate}
                    />
                )}

                <LoadsDispatchItemsModal
                    show={this.state.dispatchModalOpen}
                    onClose={() => this.setState({dispatchModalOpen: false, dispatchModalItem: null})}
                    selectedItem={this.state.dispatchModalItem}
                    translate={translate}
                />

                {this.state.recalculateLoadID && this.state.recalculateStopsCombined && (
                    <RecalculateLoadMileage
                        LoadID={this.state.recalculateLoadID}
                        truckID={this.getID()}
                        stopsCombined={this.state.recalculateStopsCombined}
                        fetchLoadData={this.fetchData}
                        isLoading={resource.isLoading}
                        isLoadDelivered={false}
                        translate={translate}
                        isOpen={this.state.recalculateOpen}
                        onDialogClose={() => this.setState({recalculateOpen: false, recalculateLoadID: null, recalculateStopsCombined: null})}
                        initialSelectedTruckLocationID={this.state.recalculatePrevLastTruckLocationID}
                    />
                )}

                <ModalDefault
                    show={this.state.bulkRecalculateOpen}
                    widthClass="max-w-lg"
                    title="Re-calculate empty mileage"
                    onClose={() => this.setState({bulkRecalculateOpen: false})}
                    closeButtonLabel={translate('btn.close')}
                    buttonLabel={this.state.bulkRecalculateDone ? undefined : "Re-calculate"}
                    buttonDisabled={this.state.bulkRecalculateLoading || !this.state.bulkRecalculateLoads.some(l => l.isFlagged && l.selected && l.TruckLocationID != null)}
                    onButtonClick={this.handleBulkRecalculateSubmit}
                >
                    <div className="p-4">
                        {this.state.bulkRecalculateDone ? (
                            <div className="py-6 text-center text-green-600 font-semibold">
                                Re-calculation complete.
                            </div>
                        ) : (
                            <>
                                <p className="text-sm text-tm-gray-600 mb-4">
                                    Select which loads to re-calculate empty mileage for. Each load will use its first recorded stop as the reference point.
                                </p>
                                {this.state.bulkRecalculateLoads.length === 0 ? (
                                    <p className="text-sm text-tm-gray-500 italic">No loads found in the current view.</p>
                                ) : (
                                    <ul className="divide-y divide-tm-gray-200 border border-tm-gray-200 rounded-lg overflow-hidden">
                                        {this.state.bulkRecalculateLoads.map((load) => (
                                            <li key={load.LoadID} className={
                                                "flex items-start gap-3 px-4 py-3 " +
                                                (load.isFlagged ? "bg-yellow-50 hover:bg-yellow-100" : "bg-inverse hover:bg-tm-gray-50 opacity-60")
                                            }>
                                                <div className="pt-0.5">
                                                    {load.isFlagged && load.TruckLocationID != null ? (
                                                        <FieldSwitch
                                                            size="small"
                                                            value={load.selected}
                                                            onChange={(_, val) => {
                                                                this.setState(prev => ({
                                                                    bulkRecalculateLoads: prev.bulkRecalculateLoads.map(l =>
                                                                        l.LoadID === load.LoadID ? {...l, selected: val} : l
                                                                    )
                                                                }));
                                                            }}
                                                        />
                                                    ) : (
                                                        <div className="w-8"/>
                                                    )}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <span className="text-sm font-semibold text-tm-gray-900">#{load.LoadNumber}</span>
                                                        {load.emptyMiles != null && (
                                                            <span className="text-xs text-tm-gray-500">{load.emptyMiles.toFixed(1)} mi empty</span>
                                                        )}
                                                        {load.hasOutOfOrder && (
                                                            <span className="inline-flex items-center gap-0.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded px-1.5 py-0.5">
                                                                <InformationCircleIcon className="w-3 h-3"/>
                                                                Out of order
                                                            </span>
                                                        )}
                                                        {load.hasEmptyWarning && (
                                                            <span className="inline-flex items-center gap-0.5 text-xs font-medium text-orange-700 bg-orange-50 border border-orange-200 rounded px-1.5 py-0.5">
                                                                <ExclamationTriangleIcon className="w-3 h-3"/>
                                                                High empty miles
                                                            </span>
                                                        )}
                                                        {load.hasStale && (
                                                            <span className="inline-flex items-center gap-0.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded px-1.5 py-0.5">
                                                                <ExclamationCircleIcon className="w-3 h-3"/>
                                                                Wrong reference
                                                            </span>
                                                        )}
                                                    </div>
                                                    {load.isFlagged && load.TruckLocationID == null && (
                                                        <div className="mt-1 text-xs text-tm-gray-400 italic">
                                                            No previous location — cannot re-calculate
                                                        </div>
                                                    )}
                                                    {load.calculatedFrom && (
                                                        <div className="mt-1 text-xs text-tm-gray-500 space-y-0.5">
                                                            <div><span className="text-tm-gray-400">From:</span> {load.calculatedFrom}</div>
                                                            <div><span className="text-tm-gray-400">→ Use:</span> <span className="text-primary font-medium">{load.shouldUse}</span></div>
                                                        </div>
                                                    )}
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                {this.state.bulkRecalculateLoading && (
                                    <div className="flex justify-center pt-4"><Loader/></div>
                                )}
                            </>
                        )}
                    </div>
                </ModalDefault>
            </div>
        )
    }
}

export default LocationsTab;
